import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Menu, X, Phone } from 'lucide-react'
import { LanguageSelector } from '@/components/LanguageSelector'
import { LocalizedLink } from '@/components/LocalizedLink'
import { MobileMenu } from './MobileMenu'
import { cn } from '@/lib/utils'
import type { SupportedLanguage } from '@/i18n'
import { localizedRoutes, type RouteKey } from '@/i18n/routes'

interface NavItem {
  label: string
  routeKey: RouteKey
}

const PAGES_WITH_HERO: RouteKey[] = ['home', 'azienda', 'persone', 'servizi', 'contacts']

export function Header() {
  const { t, i18n } = useTranslation('common')
  const location = useLocation()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)

  const currentLang = (i18n.language || 'it') as SupportedLanguage
  const currentPath = location.pathname.replace(`/${currentLang}/`, '').replace(`/${currentLang}`, '')

  // Determine if current page has a hero (transparent header)
  const hasHero = PAGES_WITH_HERO.some((key) => {
    const route = localizedRoutes[key][currentLang]
    return route === currentPath
  })
  const isTransparent = hasHero && !isScrolled

  const navItems: NavItem[] = [
    { label: t('nav.home'), routeKey: 'home' },
    { label: t('nav.azienda'), routeKey: 'azienda' },
    { label: t('nav.persone'), routeKey: 'persone' },
    { label: t('nav.servizi'), routeKey: 'servizi' },
    { label: t('nav.contacts'), routeKey: 'contacts' },
  ]

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20)
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setIsMenuOpen(false)
  }, [location.pathname])

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [isMenuOpen])

  const isActive = (routeKey: RouteKey) => {
    const slug = localizedRoutes[routeKey][currentLang]
    return slug === currentPath
  }

  return (
    <>
      <header
        className={cn(
          'fixed left-0 right-0 top-0 z-[1000] transition-all duration-300',
          isTransparent
            ? 'bg-ink/70 backdrop-blur-md border-b border-white/5'
            : 'bg-white/95 backdrop-blur-md border-b border-ink/5 shadow-[0_4px_24px_rgba(8,24,33,0.06)]'
        )}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <nav className="flex h-18 items-center justify-between">
            {/* Logo */}
            <LocalizedLink
              to="home"
              aria-label={t('aria.goToHomepage')}
              className="relative z-10 flex items-center"
            >
              <img
                src={isTransparent ? '/media/images/life-logo-menu-w.png' : '/media/images/life-logo-1.png'}
                alt={t('aria.logoAlt')}
                className="h-10 w-auto transition-all duration-300"
              />
            </LocalizedLink>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navItems.map((item) => (
                <LocalizedLink
                  key={item.routeKey}
                  to={item.routeKey}
                  className={cn(
                    'relative px-4 py-2 text-[15px] font-medium rounded-lg transition-all duration-200',
                    isTransparent
                      ? isActive(item.routeKey)
                        ? 'text-white'
                        : 'text-white/80 hover:text-white hover:bg-white/10'
                      : isActive(item.routeKey)
                        ? 'text-primary-600'
                        : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100/60'
                  )}
                >
                  {item.label}
                  {isActive(item.routeKey) && (
                    <span
                      aria-hidden="true"
                      className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 flex items-center gap-1"
                    >
                      <span className={cn('h-px w-3 rounded-full', isTransparent ? 'bg-primary-400' : 'bg-primary-600')} />
                      <span className={cn('h-1.5 w-1.5 rounded-full border', isTransparent ? 'border-primary-400' : 'border-primary-600')} />
                      <span className={cn('h-px w-3 rounded-full', isTransparent ? 'bg-primary-400' : 'bg-primary-600')} />
                    </span>
                  )}
                </LocalizedLink>
              ))}
            </div>

            {/* Desktop Right Section */}
            <div className="hidden lg:flex items-center gap-3">
              <LanguageSelector isScrolled={!isTransparent} />

              <LocalizedLink
                to="contacts"
                className={cn(
                  'inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-full transition-all duration-200',
                  'bg-primary-400 text-ink hover:bg-primary-300 shadow-lg shadow-primary-500/25 hover:-translate-y-0.5'
                )}
              >
                <Phone className="h-4 w-4" />
                {t('buttons.contactUs')}
              </LocalizedLink>
            </div>

            {/* Mobile Hamburger */}
            <button
              className={cn(
                'lg:hidden flex items-center justify-center w-11 h-11 rounded-xl transition-all duration-200',
                isTransparent
                  ? 'text-white bg-white/10 hover:bg-white/20'
                  : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
              )}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? t('aria.closeMenu') : t('aria.openMenu')}
            >
              {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </nav>
        </div>
      </header>

      {/* Mobile Menu */}
      <MobileMenu
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        navItems={navItems}
        isActive={isActive}
      />

      {/* Header spacer */}
      <div className="h-18" />
    </>
  )
}
